// deviceInfo.ts
// Utility to get actual device information for profile creation

import { Platform, Dimensions } from 'react-native';
import Constants from 'expo-constants';
import * as Device from 'expo-device';

// Legacy simple device info for profile creation compatibility
export interface SimpleDeviceInfo {
  appVersion: string;
  deviceInfo: string;
  platform: 'Web' | 'iOS' | 'Android';
  storeId: string;
  timezone: string;
  startingAppVersion: string;
}

// Comprehensive device info for logging and profile (matches DeviceInfoSchema in PrivateProfile)
export interface ComprehensiveDeviceInfo {
  // Platform basics
  platform: string;
  osName?: string;
  osVersion?: string;
  deviceInfo?: string;
  
  // Device details (native only)
  deviceName?: string;
  deviceModel?: string;
  deviceBrand?: string;
  deviceManufacturer?: string;
  deviceYearClass?: string | number;
  deviceType?: string;
  isDevice?: boolean;
  
  // Screen info
  screenWidth?: number;
  screenHeight?: number;
  pixelRatio?: number;
  
  // Location
  countryCode?: string;
  
  // Tracking IDs
  storeId?: string;
  appsFlyerId?: string;
  
  // Metadata
  lastUpdated: number;
  appVersionWhenCaptured?: string;
}

/**
 * Get simple device information for profile creation
 * Returns real values instead of placeholders
 * @deprecated Use getComprehensiveDeviceInfo() for new code
 */
export const getDeviceInfo = (): SimpleDeviceInfo => {
  // App version from expo config or package.json
  const appVersion = Constants.expoConfig?.version || '1.0.0';
  
  // Format as required by Firestore rules (XXX.XXX.XXX)
  const formattedVersion = appVersion.split('.').map(n => n.padStart(3, '0')).join('.');
  
  // Platform-specific values
  let platform: 'Web' | 'iOS' | 'Android';
  let storeId: string;
  let deviceInfo: string;
  
  if (Platform.OS === 'ios') {
    platform = 'iOS';
    storeId = Constants.expoConfig?.ios?.bundleIdentifier || 'com.gethabitcoach.ios2';
    deviceInfo = `${Constants.deviceName || 'iPhone'} (iOS ${Platform.Version})`;
  } else if (Platform.OS === 'android') {
    platform = 'Android';
    storeId = Constants.expoConfig?.android?.package || 'com.gethabitcoach.android';
    deviceInfo = `${Constants.deviceName || 'Android Device'} (Android ${Platform.Version})`;
  } else {
    platform = 'Web';
    storeId = 'web-store';
    deviceInfo = `Web Browser (${navigator.userAgent.split(' ').pop() || 'Unknown'})`;
  }
  
  // Get timezone from device
  let timezone: string;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback to UTC if timezone detection fails
    timezone = 'UTC';
    console.warn('[DeviceInfo] Could not detect timezone, using UTC:', error);
  }
  
  const result = {
    appVersion: formattedVersion,
    deviceInfo,
    platform,
    storeId,
    timezone,
    startingAppVersion: formattedVersion,
  };

  return result;
};

/**
 * Get comprehensive device information for user profile
 * Collects all device details once to be saved to profile, not sent with every log
 */
export const getComprehensiveDeviceInfo = (): ComprehensiveDeviceInfo => {
  const now = Math.floor(Date.now() / 1000);
  const deviceInfo: ComprehensiveDeviceInfo = {
    lastUpdated: now,
  };
  
  try {
    // App version
    const appVersion = Constants.expoConfig?.version || '1.0.0';
    deviceInfo.appVersionWhenCaptured = appVersion;
    
    // Platform info
    deviceInfo.platform = Platform.OS === 'web' ? 'Web' : Platform.OS === 'ios' ? 'iOS' : 'Android';
    
    // Store ID
    if (Platform.OS === 'ios') {
      deviceInfo.storeId = Constants.expoConfig?.ios?.bundleIdentifier || 'com.gethabitcoach.ios2';
    } else if (Platform.OS === 'android') {
      deviceInfo.storeId = Constants.expoConfig?.android?.package || 'com.gethabitcoach.android';
    } else {
      deviceInfo.storeId = 'web-store';
    }
    
    // OS version (similar to iOS "iOS v17.2" format)
    if (Platform.OS !== 'web') {
      const osName = Device.osName || Platform.OS;
      const osVersion = Device.osVersion || String(Platform.Version);
      deviceInfo.deviceInfo = `${osName} v${osVersion}`;
      deviceInfo.osName = osName;
      deviceInfo.osVersion = osVersion;
    } else {
      deviceInfo.deviceInfo = 'Web';
      deviceInfo.osName = 'Web';
    }
    
    // Device details (native only)
    if (Platform.OS !== 'web') {
      deviceInfo.deviceName = Device.deviceName || 'Unknown';
      deviceInfo.deviceModel = Device.modelName || Device.modelId || 'Unknown';
      deviceInfo.deviceBrand = Device.brand || 'Unknown';
      deviceInfo.deviceManufacturer = Device.manufacturer || 'Unknown';
      deviceInfo.deviceYearClass = Device.deviceYearClass || 'Unknown';
      
      // Device type
      const deviceType = Device.deviceType;
      if (deviceType !== undefined && deviceType !== null) {
        const deviceTypeMap: Record<number, string> = {
          1: 'Phone',
          2: 'Tablet',
          3: 'Desktop',
          4: 'TV',
        };
        deviceInfo.deviceType = deviceTypeMap[deviceType] || 'Unknown';
      }
      
      // Is physical device or emulator
      deviceInfo.isDevice = Device.isDevice;
    }
    
    // Screen info
    try {
      const { width, height, scale } = Dimensions.get('window');
      deviceInfo.screenWidth = width;
      deviceInfo.screenHeight = height;
      deviceInfo.pixelRatio = scale;
    } catch {}
    
    // Country code (from locale - not perfect but better than nothing)
    // Note: Logs get more accurate countryCode from backend (IP-based)
    try {
      const locale = Intl.DateTimeFormat().resolvedOptions().locale;
      deviceInfo.countryCode = locale?.split('-')[1]?.toLowerCase() || '';
    } catch {}
    
    if (__DEV__) {
      console.log('📱 Comprehensive Device Info Collected:', deviceInfo);
    }
  } catch (error) {
    console.error('Failed to collect comprehensive device info:', error);
  }
  
  return deviceInfo;
};

